import { createReducer } from '@reduxjs/toolkit';
import { adapter, initialState } from './state';
import {
    getProductsAction,
    checkoutAction,
    submitWithoutPaymentAction,
    addToCartAction,
    removeFromCartAction,
    updateCartItemAction,
    updateCartItemLearnerAction,
    clearCartAction,
    clearAllAction,
    clearErrorsAction,
    checkProformaStatusAction,
    clearProformaStatusAction,
    setPendingProformaAction,
} from './actions';
import {
    setSelectedIdCustomer,
    clearAllAction as clearAllCustomersAction
} from '../customers/actions';

export const reducer = createReducer(initialState, builder =>
    builder
        .addCase(getProductsAction.pending, (state, action) => {
            return {
                ...state,
                isFetching: true,
            };
        })
        .addCase(getProductsAction.fulfilled, (state, action) => {
            const { results } = action.payload;
            const { products } = results;
            return adapter.setAll({ ...state, isFetching: false, hasFetched: true }, products);
        })
        .addCase(getProductsAction.rejected, (state, action) => {
            return {
                ...state,
                isFetching: false,
                hasFetched: true,
                errorMessage: (action.payload as any)?.errorMessage || 'Failed to load services'
            };
        })

        .addCase(checkProformaStatusAction.pending, (state) => {
            return { ...state, isFetching: true, errorMessage: undefined, proformaStatus: undefined };
        })
        .addCase(checkProformaStatusAction.fulfilled, (state, action) => {
            return {
                ...state,
                isFetching: false,
                proformaStatus: {
                    isPaid: action.payload.results.isPaid,
                    isFulfilled: action.payload.results.isFulfilled,
                    fulfilmentFailed: action.payload.results.fulfilmentFailed,
                    nextAction: action.payload.results.nextAction,
                    canCheckAgain: action.payload.results.canCheckAgain,
                    canBook: action.payload.results.canBook,
                    message: action.payload.results.message,
                    bookableProfilePackageId: action.payload.results.bookableProfilePackageId,
                    error: action.payload.results.error,
                    checkedAt: new Date().toISOString(),
                },
                proforma: action.payload.results.proforma || state.proforma,
            };
        })
        .addCase(checkProformaStatusAction.rejected, (state, action) => {
            return {
                ...state,
                isFetching: false,
                errorMessage: (action.payload as any)?.errorMessage || 'Failed to check proforma status',
                proformaStatus: {
                    isPaid: false,
                    error: true,
                    // A transient verify failure must not be a dead-end: keep the
                    // "Check Again" affordance available so the user can retry.
                    canCheckAgain: true,
                    checkedAt: new Date().toISOString(),
                }
            };
        })

        .addCase(checkoutAction.pending, (state) => {
            return { ...state, isFetching: true, errorMessage: undefined, paymentUrl: undefined, proforma: undefined, proformaStatus: undefined };
        })
        .addCase(checkoutAction.fulfilled, (state, action) => {
            return { ...state, isFetching: false, cart: [], paymentUrl: action.payload.results.paymentUrl, proforma: action.payload.results.proforma };
        })
        .addCase(checkoutAction.rejected, (state, action) => {
            return {
                ...state,
                isFetching: false,
                errorMessage: (action.payload as any)?.errorMessage || 'Failed to complete checkout'
            };
        })

        .addCase(submitWithoutPaymentAction.pending, (state) => {
            return { ...state, isFetching: true, errorMessage: undefined, paymentUrl: undefined, proforma: undefined, proformaStatus: undefined };
        })
        .addCase(submitWithoutPaymentAction.fulfilled, (state, action) => {
            return { ...state, isFetching: false, cart: [], proforma: action.payload.results.proforma };
        })
        .addCase(submitWithoutPaymentAction.rejected, (state, action) => {
            return {
                ...state,
                isFetching: false,
                errorMessage: (action.payload as any)?.errorMessage || 'Failed to submit order'
            };
        })

        // Cart Reducers
        .addCase(addToCartAction, (state, action) => {
            const existingIndex = state.cart.findIndex(item => item.id === action.payload.id);
            if (existingIndex > -1) {
                const newCart = [...state.cart];
                newCart[existingIndex] = {
                    ...newCart[existingIndex],
                    quantity: newCart[existingIndex].quantity + action.payload.quantity
                };
                return { ...state, cart: newCart, paymentUrl: undefined };
            }
            return { ...state, cart: [...state.cart, action.payload], paymentUrl: undefined };
        })
        .addCase(removeFromCartAction, (state, action) => {
            return { ...state, cart: state.cart.filter(item => item.id !== action.payload) };
        })
        .addCase(updateCartItemAction, (state, action) => {
            const { id, quantity } = action.payload;
            return {
                ...state,
                cart: state.cart.map(item =>
                    item.id === id ? { ...item, quantity: Math.max(1, quantity) } : item
                )
            };
        })
        .addCase(updateCartItemLearnerAction, (state, action) => {
            const { id, learnerId } = action.payload;
            const itemIndex = state.cart.findIndex(item => item.id === id);
            if (itemIndex === -1) return state;

            const item = state.cart[itemIndex];
            const newId = `${item.product.productKey}:${learnerId ?? 'guest'}`;

            // If newId already exists in cart and it's not the same item, merge them
            const existingIndex = state.cart.findIndex(i => i.id === newId);

            if (existingIndex > -1 && existingIndex !== itemIndex) {
                const newCart = [...state.cart];
                const existingItem = newCart[existingIndex];
                newCart[existingIndex] = {
                    ...existingItem,
                    quantity: existingItem.quantity + item.quantity
                };
                return {
                    ...state,
                    cart: newCart.filter((_, idx) => idx !== itemIndex)
                };
            }

            return {
                ...state,
                cart: state.cart.map(i =>
                    i.id === id ? { ...i, id: newId, learnerId } : i
                )
            };
        })
        .addCase(clearCartAction, state => {
            return { ...state, cart: [] };
        })
        .addCase(clearProformaStatusAction, state => {
            return { ...state, proformaStatus: undefined, proforma: undefined, isFetching: false };
        })
        .addCase(setPendingProformaAction, (state, action) => {
            return { ...state, paymentUrl: action.payload.paymentUrl, proforma: action.payload.proforma };
        })

        /** HANDLE NON-API ACTIONS ====================================== */
        .addCase(clearAllAction.fulfilled, () => adapter.removeAll({ ...initialState }))
        .addCase(clearErrorsAction.fulfilled, state => ({ ...state, errorMessage: undefined }))

        /** EXTERNAL ACTIONS =================================== */
        .addCase(setSelectedIdCustomer.fulfilled, () => adapter.removeAll({ ...initialState }))
        .addCase(clearAllCustomersAction.fulfilled, () => adapter.removeAll({ ...initialState }))
);
